<template>
  <div class="formDetailReportComponent">
    <div class="ContainerRecordatorio">
      <div class="ContainerRecorda">
        <v-icon color="#f88623" x-large>mdi-alert-circle</v-icon>
        <p>
          <span>
            {{ $t('report.label.starTax.description') }}
          </span>
        </p>
      </div>
      <v-form ref="formulario" lazy-validation class="report-form mt-5">
        <div class="formReport">
          <v-row no-gutters>
            <v-col cols="12" sm="6" class="pr-sm-3">
              <v-select
                v-model="formData.year"
                :items="years"
                :label="'*' + $t('report.label.yearDate')"
                :rules="yearRules"
                color="secondary"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" class="pl-sm-3">
              <v-select
                v-model="formData.month"
                :items="availableMonths"
                item-text="text"
                item-value="value"
                :label="'*' + $t('report.label.monthDate')"
                :rules="monthRules"
                color="secondary"
              ></v-select>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="formData.year && formData.month">
            <v-col cols="12">
              <p class="cycle-info">
                <strong>{{ $t('report.label.starTax.cycle') }}:</strong>
                {{ cycleRange }}
              </p>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </div>

    <!-- Botón flotante de descarga -->
    <v-tooltip left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          fab
          fixed
          bottom
          right
          dark
          large
          color="primary"
          v-bind="attrs"
          v-on="on"
          @click="submitForm"
          style="margin-bottom: 70px; z-index: 1000;"
        >
          <v-icon dark>mdi-download</v-icon>
        </v-btn>
      </template>
      <span>{{ $t('button.download') }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Swal from "sweetalert2";
import i18n from '@/plugins/i18n';
import {
  REPORTS_M,
  ACTION_GET_DOWLOAD_STAR_TAX_REPORT,
  ACTION_GET_STAR_TAX_REPORT_JOB_STATUS,
} from "@/views/reports/store/types/types.js";
import {
  COMPANIES_M,
  ACTION_GET_COMPANY_BY_ID,
} from "@/views/companies/store/types/types.js";

export default {
  name: "formStarTaxReportComponent",
  props: {
    report: {
      required: true,
    },
  },
  data() {
    return {
      formData: {
        year: null,
        month: null,
      },
      jobId: null,
      pollingInterval: null,
      processingJob: false,
      months: [],
      years: [],
      cutoffDay: 21,
      yearRules: [
        v => !!v || i18n.t('report.label.yearDate.required'),
      ],
      monthRules: [
        v => v !== null && v !== undefined || i18n.t('report.label.monthDate.required'),
        v => this.availableMonths.some(m => m.value === v) || i18n.t('report.label.monthDate.future'),
      ],
    };
  },

  computed: {
    cycleRange() {
      if (!this.formData.year || !this.formData.month) return '';

      const year = Number(this.formData.year);
      const month = Number(this.formData.month);
      const day = this.cutoffDay;

      const currentMonthLastDay = new Date(year, month, 0).getDate();
      const endDay = Math.min(day, currentMonthLastDay);
      const endDate = new Date(year, month - 1, endDay);

      const previousMonthLastDay = new Date(year, month - 1, 0).getDate();
      const startDate = new Date(year, month - 2, Math.min(day, previousMonthLastDay));
      startDate.setDate(startDate.getDate() + 1);

      const format = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      return `${format(startDate)} - ${format(endDate)}`;
    },

    availableMonths() {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      if (Number(this.formData.year) !== currentYear) {
        return this.months;
      }
      return this.months.filter(m => {
        if (m.value < currentMonth) return true;
        if (m.value === currentMonth && today.getDate() > this.cutoffDay) return true;
        return false;
      });
    },
  },

  watch: {
    'formData.year'() {
      const available = this.availableMonths;
      if (this.formData.month && !available.some(m => m.value === this.formData.month)) {
        this.formData.month = available.length ? available[available.length - 1].value : null;
      }
    },
  },

  mounted() {
    this.loadYearsAndMonths();
    this.loadCompanyCutoffDay();
  },

  beforeDestroy() {
    this.stopPolling();
  },


  methods: {
    ...mapActions({
      generateStarTaxReport: `${REPORTS_M}${ACTION_GET_DOWLOAD_STAR_TAX_REPORT}`,
      getStarTaxReportJobStatus: `${REPORTS_M}${ACTION_GET_STAR_TAX_REPORT_JOB_STATUS}`,
      getCompanyById: `${COMPANIES_M}${ACTION_GET_COMPANY_BY_ID}`,
    }),

    async loadCompanyCutoffDay() {
      try {
        const idEmpresa = sessionStorage.getItem('idEmpresa');
        if (!idEmpresa) return;
        const resp = await this.getCompanyById(idEmpresa);
        this.cutoffDay = resp?.monthlyCutoffDay ?? 21;
      } catch (error) {
        this.cutoffDay = 21;
      }
    },

    loadYearsAndMonths() {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      this.years = [currentYear, currentYear - 1, currentYear - 2];

      this.months = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        text: (i + 1).toString(),
      }));

      if (currentMonth === 1) {
        this.formData.year = currentYear - 1;
        this.formData.month = 12;
      } else {
        this.formData.year = currentYear;
        this.formData.month = currentMonth - 1;
      }
    },

    async submitForm() {
      const validationErrors = this.validateForm();

      if (Object.keys(validationErrors).length > 0) {
        const errorMessages = Object.values(validationErrors).join('<br>');
        Swal.fire({
          icon: 'error',
          title: i18n.t('label.errorsValidation.plural.title'),
          html: '<div class="swal-subtitle"><b>' + i18n.t('label.errorsValidation.text') + ':</b></div><br>' + errorMessages,
          confirmButtonColor: '#6E17A5',
        });
        return;
      }

      try {
        const response = await this.generateStarTaxReport({
          year: this.formData.year,
          month: this.formData.month,
        });

        if (response.status === 202 && response.jobId) {
          this.jobId = response.jobId;
          this.processingJob = true;

          Swal.fire({
            title: i18n.t('report.label.processing.title') || 'Generando reporte...',
            text: i18n.t('report.label.processing.text') || 'Por favor espera mientras se genera el archivo.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          this.startPolling();
          return;
        }

        if (response.data && response.data.payload && response.data.payload.url) {
          this.downloadExcelFile(response.data.payload.url);
        } else {
          Swal.fire({
            icon: 'info',
            title: i18n.t('report.label.downloadExcel.title'),
            text: (response.data && response.data.payload && response.data.payload.message) || 'No hay información para descargar',
          });
        }
      } catch (error) {
        console.error(i18n.t('report.label.downloadExcel.error'), error);
        Swal.fire({
          icon: 'error',
          title: i18n.t('report.label.error.title') || 'Error',
          text: i18n.t('report.label.processing.error') || 'Error al generar el reporte',
          confirmButtonColor: '#6E17A5',
        });
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        try {
          const resp = await this.getStarTaxReportJobStatus(this.jobId);
          const jobStatus = resp?.data?.payload;

          if (!jobStatus) return;

          if (jobStatus.jobStatus === 2) {
            this.stopPolling();

            Swal.close();

            if (jobStatus.linkFile) {
              window.open(jobStatus.linkFile, '_blank');
            }

            this.processingJob = false;
          } else if (jobStatus.jobStatus === 3) {
            this.stopPolling();

            Swal.close();

            Swal.fire({
              title: i18n.t('report.label.error.title') || 'Error',
              text: jobStatus.errorMessage || i18n.t('report.label.processing.error') || 'Error al generar el reporte',
              icon: 'error',
              confirmButtonText: i18n.t('button.ok'),
            });

            this.processingJob = false;
          }
        } catch (error) {
          this.stopPolling();

          Swal.close();

          Swal.fire({
            title: i18n.t('report.label.error.title') || 'Error',
            text: i18n.t('report.label.polling.error') || 'Error al consultar el estado del reporte',
            icon: 'error',
            confirmButtonText: i18n.t('button.ok'),
          });

          this.processingJob = false;
        }
      }, 3000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    downloadExcelFile(responseUrl) {
      const link = document.createElement('a');
      link.href = responseUrl;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    validateForm() {
      const errors = {};

      if (!this.formData.year) {
        errors.year = i18n.t('report.label.yearDate.required');
      }

      if (this.formData.month === null || this.formData.month === undefined) {
        errors.month = i18n.t('report.label.monthDate.required');
      }

      return errors;
    },
  },
};
</script>

<style scoped>
.formDetailReportComponent {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.ContainerRecordatorio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 760px;
  margin: 20px auto;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(110, 23, 165, 0.1);
  padding: 28px;
}

.ContainerRecorda {
  background-color: rgba(248, 134, 35, 0.12);
  width: 100%;
  min-height: 64px;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
}

.ContainerRecordatorio p {
  margin-bottom: 0 !important;
  text-align: left;
  line-height: 1.5;
}

p span {
  color: #6e17a5;
  font-weight: 600;
  font-size: 15px;
}

.report-form {
  width: 100%;
}

.formReport {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cycle-info {
  font-size: 16px;
  color: #6e17a5;
  font-weight: 500;
  margin: 12px 0 0;
  text-align: center;
  padding: 12px 16px;
  background-color: #f5edf9;
  border-radius: 10px;
}

.swal-subtitle {
  font-size: 14px;
  margin-bottom: 10px;
  color: #666;
}
</style>
